import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Shipment, ShipmentDetail, ShipmentDetailAllocation, ShipmentService } from 'src/app/acumatica/EntityServices/Shipment.service';
import { StockItem, StockItemService } from 'src/app/acumatica/EntityServices/StockItem.service';
import { LoadingIndicator } from 'src/app/acumatica/loading-indicator/AcumaticaLoadingOptions';
import { ApplicationScreen } from 'src/app/application-navigator/application-screen';
import { AlertDialog } from 'src/app/common/alert-dialog/alert-dialog.component';
import { AudioService, Sound } from 'src/app/services/audio.service';

@Component({
  selector: 'app-cut-hold-pull',
  templateUrl: './cut-hold-pull.component.html',
  styleUrls: ['./cut-hold-pull.component.css']
})
export class CutHoldPullComponent extends ApplicationScreen implements OnInit, OnDestroy {
  pageTitle = "Cut & Hold Pull";
  stockItems: { [key: string]: StockItem } = {};
  stockItemGetters: { [key: string]: Promise<StockItem> } = {};

  @ViewChild('releasingFromHoldIndicator') releasingFromHoldIndicator!: TemplateRef<any>;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private snackbar: MatSnackBar,
    private audio: AudioService,
    private shipmentService: ShipmentService,
    private stockItemService: StockItemService,
  ) {
    super();

    // get the shipment number from the navigation router
    this.route.paramMap.subscribe(params => {
      this.shipmentNbr = params.get('shipmentNbr');
    });
  }

  ngOnInit(): void { }

  ngOnDestroy(): void {
    this.onScannerInput.unsubscribe();
  }

  receiveScannerInput(data: string | object) {
    if (data instanceof String) {
      this.audio.play(Sound.ErrorBeep);
      this.snackbar.open("Invalid QR Scan", undefined, { duration: 5000 });
      return;
    }

    let scan = <ItemTagQR>data;
    let piece = this.shipmentPieces.find(piece => piece.InventoryID == scan.ITM && piece.LotSerialNbr == scan.LOT && piece.checked != true);
    if (piece === undefined) {
      this.audio.play(Sound.ErrorBeep);
      this.snackbar.open("Item not found in shipment unscanned details", undefined, { duration: 5000 });
      return;
    }

    piece.checked = true;
  }

  public shipment?: Shipment;
  /** collection of all individual Allocations/Pieces on the Shipment */
  public shipmentPieces: ShipmentDetailAllocationCHPull[] = [];

  private _shipmentNbr: string | null = null;
  public get shipmentNbr() { return this._shipmentNbr; }
  public set shipmentNbr(value: string | null) {
    this._shipmentNbr = value;

    if (value == null) {
      this.shipment = undefined;
    } else {
      // get the Shipment
      this.shipmentService.get(value).subscribe(shipment => {

        // validate it's a Cut & Hold shipment
        if (!shipment.CutAndHold) {
          this.dialog.open(AlertDialog, { data: { message: "Shipment is not a Cut & Hold Shipment" } })
            .afterClosed().subscribe(() => {
              this.router.navigate(['/holdpull']);
            });
          return;
        }

        // initialize data for Pull
        this.shipment = shipment;
        this.shipmentPieces = [];

        // initialize ShipmentDetailAllocationCHPull objects for non-allowance details
        shipment.Details.filter(detail => !detail.FreeItem).forEach(detail => {
          detail.Allocations.forEach(alloc => {
            let CHAlloc = <ShipmentDetailAllocationCHPull>alloc;
            CHAlloc.shipmentDetail = detail;
            this.shipmentPieces.push(CHAlloc);
          })
        });

        // enable Scan receiver
        this.onScannerInput.subscribe(data => this.receiveScannerInput(data));

        // sequentially fetch Stock Item objects
        let inventoryIDs = shipment.Details.map(detail => detail.InventoryID).unique<string>();
        const nextItem = () => {
          const inventoryID = inventoryIDs.shift();
          if (inventoryID === undefined) return;
          this.stockItemService.get(inventoryID, { loader: { message: `Getting Item ${inventoryID}`, indicator: LoadingIndicator.snackbar } })
            .subscribe(stockItem => {
              if (stockItem === undefined) throw new Error(`Unable to load Stock Item ${inventoryID}`);
              this.stockItems[inventoryID] = stockItem;
              nextItem();
            });
        }
        nextItem();
      });
    }
  }

  shipmentPieceTableColumns = ['checked', 'thumbnail', 'Item', 'Qty'];

  /** boolean value indicates if all Pieces on the shipment have been 'checked' */
  public get allChecked(): boolean { return this.shipmentPieces.reduce((prev: boolean, detail: ShipmentDetailAllocationCHPull) => prev && detail.checked, true); }

  completeCutHoldPull() {
    // if the Shipment is on Hold, [Remove Hold]
    if (this.shipment?.Status == 'On Hold') {

      let dialogRef = this.dialog.open(this.releasingFromHoldIndicator);

      this.shipmentService.ReleaseFromHold(this.shipment)
        .then(() => { })
        .catch((err) => {
          this.dialog.open(AlertDialog, { data: { message: "An error occurred attemping to Release the Shipment from Hold" } });
        })
        .finally(() => {
          dialogRef.close();
          this.router.navigate(['/holdpull']);
        })
    } else {
      this.router.navigate(['/holdpull']);
    }
  }
}

/** extended class containing reference to original ShipmentDetail object the Piece is associated with */
class ShipmentDetailAllocationCHPull extends ShipmentDetailAllocation {
  public checked: boolean = false;
  public shipmentDetail!: ShipmentDetail;
}

interface ItemTagQR {
  ITM: string,
  LOT: string
}